import * as React from 'react';
import Image from 'next/image';
import tw, { styled, css } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { PencilSquareIcon, MapPinIcon, BriefcaseIcon, PlusIcon } from '@heroicons/react/24/outline';
import { ArrowsRightLeftIcon } from '@heroicons/react/24/solid';
import { ButtonV2, Link } from '@restworld/ui-ds';
import { paymentProviderLogoMap } from '@restworld/utils-common';
import { Company } from '@restworld/utility-types';

export interface PaymentMethodInfo {
  id: string;
  brand?: string;
  last4?: string;
  type?: string;
  default?: boolean;
}

export interface CompanyPaymentInfoCardProps {
  company: Company;
  paymentMethods?: PaymentMethodInfo[];
  orientation?: 'vertical' | 'horizontal';
  onEditCompany?: (company: Company) => void;
  onChangePaymentMethod?: (company: Company) => void;
  editLabel?: string;
  changeLabel?: string;
  billingInfoLabel?: string;
  paymentMethodLabel?: string;
  vatLabel?: string;
  defaultBadgeLabel?: string;
  addLabel?: string;
}

export const CompanyPaymentInfoCard: React.FunctionComponent<CompanyPaymentInfoCardProps> = ({
  company,
  paymentMethods = [],
  orientation = 'vertical',
  onEditCompany,
  onChangePaymentMethod,
  editLabel,
  changeLabel,
  billingInfoLabel,
  paymentMethodLabel,
  vatLabel,
  defaultBadgeLabel,
  addLabel,
}) => {
  const { t } = useTranslation('jp-onboarding');

  const resolvedEditLabel = editLabel ?? t('steps.confirm_payment_method.edit');
  const resolvedChangeLabel = changeLabel ?? t('steps.confirm_payment_method.change');
  const resolvedBillingInfoLabel = billingInfoLabel ?? t('steps.confirm_payment_method.billing_info');
  const resolvedPaymentMethodLabel = paymentMethodLabel ?? t('steps.confirm_payment_method.payment_method');
  const resolvedVatLabel = vatLabel ?? t('steps.confirm_payment_method.vat_label');
  const resolvedDefaultBadgeLabel = defaultBadgeLabel ?? t('steps.confirm_payment_method.default');
  const resolvedAddLabel = addLabel ?? t('steps.confirm_payment_method.add', 'Add');

  const defaultPaymentMethod = paymentMethods.find((pm) => pm.default) || paymentMethods[0];
  const paymentBrand = defaultPaymentMethod?.brand?.toLowerCase();
  const paymentLogoSrc = paymentBrand && paymentProviderLogoMap[paymentBrand];

  const isHorizontal = orientation === 'horizontal';

  return (
    <CardContainer isHorizontal={isHorizontal}>
      {/* Billing Information Section */}
      <Section>
        <SectionHeader>
          <SectionTitle>{resolvedBillingInfoLabel}</SectionTitle>
          {onEditCompany && (
            <Link StartIcon={PencilSquareIcon} newTab={false} onClick={() => onEditCompany(company)}>
              {resolvedEditLabel}
            </Link>
          )}
        </SectionHeader>

        <DetailsWrapper>
          <CompanyName>{company.name || 'N/A'}</CompanyName>

          {company.registered_office && (
            <InfoRow>
              <MapPinIcon tw="h-4 w-4 shrink-0" />
              <InfoText>{company.registered_office}</InfoText>
            </InfoRow>
          )}

          {company.vat_number && (
            <InfoRow>
              <BriefcaseIcon tw="h-4 w-4 shrink-0" />
              <InfoText>
                {resolvedVatLabel} {company.vat_number}
              </InfoText>
            </InfoRow>
          )}
        </DetailsWrapper>
      </Section>

      {/* Payment Method Section */}
      <Section isPaymentSection>
        <SectionHeader>
          <SectionTitle>{resolvedPaymentMethodLabel}</SectionTitle>
          {defaultPaymentMethod && onChangePaymentMethod && (
            <Link StartIcon={ArrowsRightLeftIcon} newTab={false} onClick={() => onChangePaymentMethod(company)}>
              {resolvedChangeLabel}
            </Link>
          )}
        </SectionHeader>

        {defaultPaymentMethod ? (
          <PaymentMethodWrapper>
            <PaymentInfo>
              {paymentLogoSrc && (
                <Image src={paymentLogoSrc} alt={paymentBrand || ''} width={23} height={16} tw="object-cover" />
              )}
              <PaymentDetails>
                {defaultPaymentMethod.brand} ***{defaultPaymentMethod.last4 || '****'}
              </PaymentDetails>
            </PaymentInfo>
            <DefaultBadge>{resolvedDefaultBadgeLabel}</DefaultBadge>
          </PaymentMethodWrapper>
        ) : (
          <ButtonV2 variant="tertiary" fullwidth StartIcon={PlusIcon} onClick={() => onChangePaymentMethod?.(company)}>
            {resolvedAddLabel}
          </ButtonV2>
        )}
      </Section>
    </CardContainer>
  );
};

const CardContainer = styled.div<{ isHorizontal?: boolean }>`
  ${tw`bg-[#fafafa] border border-[#dedee1] rounded-2xl p-4 shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)] flex gap-4 w-full`}
  ${({ isHorizontal }) =>
    isHorizontal
      ? css`
          ${tw`flex-col md:flex-row md:items-stretch`}
        `
      : tw`flex-col`}
`;

const Section = styled.div<{ isPaymentSection?: boolean }>`
  ${tw`bg-[#f5f5f6] rounded-lg p-5 flex flex-col gap-6 min-w-[288px] flex-1`}
  ${({ isPaymentSection }) =>
    isPaymentSection &&
    css`
      ${tw`self-stretch`}
      & > *:last-child {
        ${tw`mt-auto`}
      }
    `}
`;

const SectionHeader = styled.div`
  ${tw`flex items-center justify-between`}
`;

const SectionTitle = styled.h3`
  ${tw`text-base font-semibold text-[#1c1c33]`}
`;

const DetailsWrapper = styled.div`
  ${tw`flex flex-col gap-2`}
`;

const InfoRow = styled.div`
  ${tw`flex items-center gap-0.5`}
`;

const InfoText = styled.div`
  ${tw`text-sm font-medium text-[#666676]`}
`;

const CompanyName = styled.div`
  ${tw`text-sm font-medium text-[#1c1c33]`}
`;

const PaymentMethodWrapper = styled.div`
  ${tw`flex items-end justify-between`}
`;

const PaymentInfo = styled.div`
  ${tw`flex flex-col gap-1`}
`;

const PaymentDetails = styled.div`
  ${tw`text-sm font-medium text-[#1c1c33]`}
`;

const DefaultBadge = styled.span`
  ${tw`inline-flex items-center justify-center px-2 py-0.5 text-sm font-medium text-[#482485] bg-[#f0ebf8] border border-[#482485] rounded`}
`;

/* ─── Empty State Card ─── */

export interface EmptyCompanyPaymentCardProps {
  onAddCompany?: () => void;
  onAddPaymentMethod?: () => void;
  hasCompany?: boolean;
  billingInfoLabel?: string;
  paymentMethodLabel?: string;
  addLabel?: string;
}

export const EmptyCompanyPaymentCard: React.FunctionComponent<EmptyCompanyPaymentCardProps> = ({
  onAddCompany,
  onAddPaymentMethod,
  hasCompany = false,
  billingInfoLabel,
  paymentMethodLabel,
  addLabel,
}) => {
  const { t } = useTranslation('payment-and-invoices');

  const resolvedBillingInfoLabel = billingInfoLabel ?? t('billing_information');
  const resolvedPaymentMethodLabel = paymentMethodLabel ?? t('payment_method');
  const resolvedAddLabel = addLabel ?? t('add');

  return (
    <CardContainer isHorizontal>
      {/* Billing Information – empty */}
      <Section>
        <SectionTitle>{resolvedBillingInfoLabel}</SectionTitle>
        <ButtonV2 variant="tertiary" fullwidth StartIcon={PlusIcon} onClick={onAddCompany}>
          {resolvedAddLabel}
        </ButtonV2>
      </Section>

      {/* Payment Method – empty (only shown if company exists) */}
      {hasCompany && (
        <EmptyPaymentSection>
          <SectionTitle>{resolvedPaymentMethodLabel}</SectionTitle>
          <ButtonV2 variant="tertiary" fullwidth StartIcon={PlusIcon} onClick={onAddPaymentMethod}>
            {resolvedAddLabel}
          </ButtonV2>
        </EmptyPaymentSection>
      )}
    </CardContainer>
  );
};

const EmptyPaymentSection = styled.div`
  ${tw`bg-[#f5f5f6] rounded-lg p-5 flex flex-col justify-between gap-6 min-w-[288px] flex-1 self-stretch`}
`;
